import { type Component } from "vue";
import { AppType } from "@allwyn/build/vue/app-type";

type ComponentModule = { default: Component };

/**
 * Glob maps built at compile time by Vite.
 *
 * - `appModules` — all `.vue` files under `src/` (the deh app).
 * - `sharedModules` — all `.vue` files under `libs/shared-components/`.
 *
 * `import.meta.glob` requires string literals; we use relative paths here because
 * alias resolution in glob patterns can be brittle in Vite 8 production builds.
 */
const appModules = import.meta.glob<ComponentModule>("../../**/*.vue");
const sharedModules = import.meta.glob<ComponentModule>("../../../../../libs/shared-components/**/*.vue");

if (import.meta.env.DEV && Object.keys(appModules).length === 0) {
    console.error("[resolve-vue-component] App component glob resolved empty. Check relative path.");
}

if (import.meta.env.DEV && Object.keys(sharedModules).length === 0) {
    console.error("[resolve-vue-component] Shared component glob resolved empty. Check relative path.");
}

/**
 * Resolves a component name to a lazy-loaded Vue component using Vite's glob imports.
 *
 * The component is wrapped in `defineAsyncComponent` so it is loaded on demand
 * (each resolved component ends up in its own chunk).
 *
 * @param name - Component path relative to the resolution root,
 *               e.g. `"components/my-widget.vue"` or `"landing-pages/photo-frame/_photo-frame.vue"`.
 * @param appType - Determines which glob map to search:
 *                  - `AppType.SharedComponents` → `libs/shared-components/`
 *                  - everything else → app-local `src/`
 * @returns A lazy-loaded Vue component
 * @throws If the component path does not match any entry in the glob map
 */
export function resolveVueComponent(name: string, appType: AppType): Promise<ComponentModule> {
    const normalizedName = name.trim().replaceAll("\\", "/").replace(/^\/+/, "");

    const isShared = appType === AppType.SharedComponents;
    const importPath = isShared ? `../../../../../libs/shared-components/${normalizedName}` : `../../${normalizedName}`;
    const modules = isShared ? sharedModules : appModules;
    const importer = modules[importPath];

    if (!importer) {
        throw new Error(`Vue component '${name}' could not be resolved from '${importPath}'.`);
    }

    return importer();
}
